import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import {
    PageLayoutCard,
    SectionsOpenByDefaultCard,
    ProductDetailsListCard,
    DescriptionTextsPerLanguageCard,
    ProductTitleAndUrlCard,
    CategoryBreadcrumbCard,
    RelatedProductsCard,
    StockAvailabilityCard,
} from '../cards';

/**
 * Body of the Product Detail Information modal — the largest of the
 * Product Detail config modals. Composes eight cards covering everything
 * from page-level layout to per-language description fields.
 *
 * value shape (each section optional):
 *   { layout:           { blocks: { … } }
 *     sectionsOpen:     { desktop: { … }, mobile: { … } }
 *     detailsList:      { identity: { … }, categories: { … }, descriptions: { … } }
 *     descriptionTexts: { nl: { … }, en: { … }, fr: { … } }
 *     titleAndUrl:      { showName, showBrand, … }
 *     breadcrumb:       { enabled, showRootLink, separator }
 *     related:          { maxToShow, variantsDisplay, showOutOfStock, highlightSelected }
 *     stock:            { showStockByStore, hideBelowQty, hideExactQty }
 *   }
 *
 * onChange(section, field, value) — same contract as the other modal bodies.
 * For cards whose state is grouped (sectionsOpen / detailsList /
 * descriptionTexts), `field` is the sub-group key and `value` is the new
 * sub-object the card emits.
 */
const ProductDetailInformationModal = ({ value = {}, onChange, shopFieldLabels, theme, t }) => {
    const cardOnChange = (section) => (field, val) => {
        if (onChange) onChange(section, field, val);
    };

    return (
        // Wide modal → flow the cards into two balanced (masonry) columns on
        // large screens so the canvas is actually used; single column otherwise.
        <Box sx={{ columnGap: 2, columns: { xs: '1', lg: '2' }, '& > *': { breakInside: 'avoid', mb: 2 } }}>
            <PageLayoutCard
                value={value.layout}
                onChange={cardOnChange('layout')}
                theme={theme}
                t={t}
            />
            <SectionsOpenByDefaultCard
                value={value.sectionsOpen}
                onChange={cardOnChange('sectionsOpen')}
                theme={theme}
                t={t}
            />
            <ProductDetailsListCard
                value={value.detailsList}
                onChange={cardOnChange('detailsList')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <DescriptionTextsPerLanguageCard
                value={value.descriptionTexts}
                onChange={cardOnChange('descriptionTexts')}
                theme={theme}
                t={t}
            />
            <ProductTitleAndUrlCard
                value={value.titleAndUrl}
                onChange={cardOnChange('titleAndUrl')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <CategoryBreadcrumbCard
                value={value.breadcrumb}
                onChange={cardOnChange('breadcrumb')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <RelatedProductsCard
                value={value.related}
                onChange={cardOnChange('related')}
                theme={theme}
                t={t}
            />
            <StockAvailabilityCard
                value={value.stock}
                onChange={cardOnChange('stock')}
                theme={theme}
                t={t}
            />
        </Box>
    );
};

ProductDetailInformationModal.propTypes = {
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    shopFieldLabels: PropTypes.object,
    theme:              PropTypes.string,
    t:                  PropTypes.func,
};

export default ProductDetailInformationModal;
